import { useEffect, useState } from "react";
import { api, fmtINR } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Store, TrendingUp, FileText, Printer, Sparkles, UserPlus, Zap, AlertTriangle } from "lucide-react";

function Kpi({ icon: Icon, label, value, sub, testid, tone = "orange" }) {
  const tones = {
    orange: "bg-orange-50 text-orange-600",
    amber: "bg-amber-50 text-amber-600",
    emerald: "bg-emerald-50 text-emerald-600",
    slate: "bg-slate-100 text-slate-600",
    red: "bg-red-50 text-red-600",
  };
  return (
    <Card data-testid={testid} className="border-slate-200">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
            <div className="mt-2 font-display text-3xl font-bold tracking-tight">{value}</div>
            {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
          </div>
          <div className={`w-10 h-10 rounded-lg grid place-items-center ${tones[tone]}`}><Icon className="w-5 h-5" /></div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [funnel, setFunnel] = useState(null);
  useEffect(() => {
    api.get("/admin/analytics").then((r) => setData(r.data));
    api.get("/admin/analytics/signup-funnel").then((r) => setFunnel(r.data)).catch(() => {});
  }, []);
  if (!data) return <div className="p-8 text-slate-400">Loading…</div>;

  const pie = [
    { name: "B&W", value: data.bw_pages, fill: "#0F172A" },
    { name: "Colour", value: data.color_pages, fill: "#F97316" },
  ];

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Platform overview</h1>
        <p className="text-slate-500">Everything happening across your SaaS in real time.</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Store} label="Total Stores" value={data.total_stores} sub={`${data.active_stores} active`} testid="kpi-stores" />
        <Kpi icon={FileText} label="Total Orders" value={data.total_orders} sub={`${data.total_pages} pages`} testid="kpi-orders" />
        <Kpi icon={TrendingUp} label="Revenue" value={fmtINR(data.total_revenue)} sub={`+${fmtINR(data.subscription_revenue)} subs`} testid="kpi-revenue" />
        <Kpi icon={Printer} label="Active Subs" value={data.active_subscriptions} sub={`${data.expired_subscriptions} expired`} testid="kpi-subs" />
      </div>

      {/* Signup funnel */}
      {funnel && (
        <Card className="border-slate-200" data-testid="signup-funnel-card">
          <CardHeader>
            <CardTitle className="font-display flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-orange-500" />
              Google sign-up funnel
              <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 font-normal ml-1">Last 30 days</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi icon={UserPlus} label="Google signups" value={funnel.google_signups} sub={`+${funnel.google_signups_30d} in 30d`} testid="funnel-signups" tone="slate" />
              <Kpi icon={Sparkles} label="Active trials" value={funnel.trial_active} sub="on 10/day cap" testid="funnel-active" tone="amber" />
              <Kpi icon={Zap} label="Upgraded to paid" value={funnel.trial_upgraded} sub="trial → paid" testid="funnel-upgraded" tone="emerald" />
              <Kpi icon={TrendingUp} label="Conversion" value={`${funnel.conversion_rate}%`} sub={`${funnel.trial_cap_nudges_30d} nudges fired`} testid="funnel-rate" tone="orange" />
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-slate-500 font-semibold mb-2">Daily Google signups</div>
                <div className="h-40">
                  {funnel.daily_signups.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={funnel.daily_signups}>
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                        <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                        <Tooltip />
                        <Line type="monotone" dataKey="count" stroke="#F97316" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : <EmptyChart label="No Google signups yet" />}
                </div>
              </div>
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-slate-500 font-semibold mb-2">Daily trial → paid upgrades</div>
                <div className="h-40">
                  {funnel.daily_upgrades.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={funnel.daily_upgrades}>
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                        <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#10B981" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <EmptyChart label="No upgrades yet — nudges will drive them" />}
                </div>
              </div>
            </div>

            {funnel.trial_cap_nudges_30d > 0 && funnel.trial_upgraded === 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 flex items-start gap-2" data-testid="funnel-no-conv-warn">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-900">
                  <b>{funnel.trial_cap_nudges_30d} shops hit the daily cap</b> in the last 30 days but none upgraded. Consider running a discount campaign or shortening the "Upgrade" CTA — nudges alone aren't converting yet.
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-slate-200">
          <CardHeader><CardTitle className="font-display">Revenue trend</CardTitle></CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.daily}>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="revenue" stroke="#F97316" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardHeader><CardTitle className="font-display">B&W vs Colour</CardTitle></CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pie} dataKey="value" nameKey="name" outerRadius={80} label>
                  {pie.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
      <Card className="border-slate-200">
        <CardHeader><CardTitle className="font-display">Revenue by store</CardTitle></CardHeader>
        <CardContent className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.stores_stats}>
              <XAxis dataKey="store" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="revenue" fill="#F97316" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}

function EmptyChart({ label }) {
  return <div className="h-full grid place-items-center text-xs text-slate-400 border border-dashed border-slate-200 rounded-lg">{label}</div>;
}
